import { useMutation } from "@tanstack/react-query";
import { fetchClient } from "../generated/client";

type TranslatePortalCustomTextParams = {
  shopId: string;
  text: string;
  sourceLocale: string;
  targetLocales: string[];
};

type TranslatePortalCustomTextResponse = {
  translated: Record<string, string>;
};

/**
 * POST `/shops/:shopId/preferences/portals/custom-texts/translate` — invokes
 * the generic AI translate use case to bulk-translate a merchant-authored
 * override into every remaining active shop language.
 *
 * Uses `fetchClient` directly because the path is not yet reflected in the
 * generated OpenAPI schema.
 */
export function useTranslatePortalCustomText() {
  return useMutation({
    mutationFn: async ({
      shopId,
      text,
      sourceLocale,
      targetLocales,
    }: TranslatePortalCustomTextParams): Promise<TranslatePortalCustomTextResponse> => {
      const client = fetchClient as unknown as {
        POST: (
          url: string,
          init: {
            params: { path: { shopId: string } };
            body: {
              text: string;
              sourceLocale: string;
              targetLocales: string[];
            };
          },
        ) => Promise<{
          data?: TranslatePortalCustomTextResponse;
          error?: unknown;
        }>;
      };

      const { data, error } = await client.POST(
        "/shops/{shopId}/preferences/portals/custom-texts/translate",
        {
          params: { path: { shopId } },
          body: { text, sourceLocale, targetLocales },
        },
      );
      if (error) throw error;
      return data as TranslatePortalCustomTextResponse;
    },
  });
}
